import { ContentPlanningProject } from '../types';

export class ContentPlanningPdfService {
  /**
   * Export Content Planning & Page Architecture Strategy PDF Report
   */
  static exportProjectPdfReport(project: ContentPlanningProject): void {
    const dateStr = new Date(project.createdAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Pop-up blocked. Please allow pop-ups to export the PDF report.');
      return;
    }

    const pages = project.pages || [];
    const completedOutlinesCount = pages.filter(p => p.finalOutline && p.finalOutline.outlineSections?.length > 0).length;
    const totalWordCount = pages.reduce((acc, p) => acc + (p.finalOutline?.recommendedWordCount || 2000), 0);

    const pagesHtml = pages.map((page, pIdx) => {
      const outline = page.finalOutline;
      const isHp = page.pageType === 'Homepage' || page.targetUrl === '/' || page.pageName.toLowerCase().includes('home');
      const pageTypeBadge = page.pageType || (isHp ? 'Homepage' : 'Service Page');

      const primaryVars = page.primaryKeywordVariations?.join(', ') || 'None';
      const secondaryVars = page.secondaryKeywordVariations?.join(', ') || 'None';
      const tertiaryVars = page.tertiaryKeywordVariations?.join(', ') || 'None';

      return `
        <div class="page-card ${pIdx > 0 ? 'page-break-before' : ''}">
          <div class="page-card-header">
            <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 10px;">
              <div>
                <span class="badge ${isHp ? 'badge-amber' : 'badge-cyan'}">
                  ${pageTypeBadge.toUpperCase()} • PAGE #${pIdx + 1}
                </span>
                <h2 class="page-title">${page.pageName}</h2>
                <div class="page-url">Target URL: <code>${page.targetUrl}</code></div>
              </div>
              <div class="word-count-badge">
                ${outline?.recommendedWordCount || 2000} Target Words
              </div>
            </div>

            <!-- KEYWORD ARCHITECTURE ROW -->
            <div class="kw-arch-box">
              <div class="kw-arch-title">KEYWORD ARCHITECTURE & TARGETING</div>
              <div class="kw-arch-grid">
                <div class="kw-item">
                  <span class="kw-label">Primary Keyword:</span>
                  <span class="kw-value">${page.primaryKeyword || 'Not set'}</span>
                  ${primaryVars !== 'None' ? `<div class="kw-sub">Variations: ${primaryVars}</div>` : ''}
                </div>
                ${page.secondaryKeyword ? `
                <div class="kw-item">
                  <span class="kw-label">Secondary Keyword:</span>
                  <span class="kw-value">${page.secondaryKeyword}</span>
                  ${secondaryVars !== 'None' ? `<div class="kw-sub">Variations: ${secondaryVars}</div>` : ''}
                </div>` : ''}
                ${page.tertiaryKeyword ? `
                <div class="kw-item">
                  <span class="kw-label">Tertiary Keyword:</span>
                  <span class="kw-value">${page.tertiaryKeyword}</span>
                  ${tertiaryVars !== 'None' ? `<div class="kw-sub">Variations: ${tertiaryVars}</div>` : ''}
                </div>` : ''}
              </div>
            </div>
          </div>

          ${outline ? `
            <!-- SEO METADATA BOX -->
            <div class="meta-grid">
              <div class="meta-box">
                <div class="meta-box-label label-blue">SEO TITLE TAG</div>
                <div class="meta-box-value">${outline.seoTitle || page.pageName}</div>
              </div>
              <div class="meta-box">
                <div class="meta-box-label label-amber">META DESCRIPTION</div>
                <div class="meta-box-value">${outline.metaDescription || 'Meta description not generated.'}</div>
              </div>
            </div>

            <!-- PRIMARY H1 HEADING CARD -->
            <div class="h1-card">
              <span class="h1-badge">PRIMARY H1 HEADING</span>
              <span class="h1-text">${outline.h1 || page.pageName}</span>
            </div>

            <!-- H2 SECTIONS BREAKDOWN -->
            <div class="sections-container">
              <div class="section-group-title">PAGE HEADINGS & CONTENT BLUEPRINT (${outline.outlineSections?.length || 0} SECTIONS)</div>
              
              ${(outline.outlineSections || []).map((sec) => `
                <div class="section-item">
                  <div class="section-item-header">
                    <span class="tag-badge ${sec.headingTag === 'H2' ? 'tag-h2' : 'tag-h3'}">${sec.headingTag}</span>
                    <span class="section-heading-text">${sec.headingText}</span>
                  </div>
                  ${sec.purpose ? `<div class="section-purpose"><strong>Purpose:</strong> ${sec.purpose}</div>` : ''}
                  ${sec.keyPoints && sec.keyPoints.length > 0 ? `
                    <div class="keypoints-list">
                      ${sec.keyPoints.map(kp => `<span class="kp-pill">• ${kp}</span>`).join('')}
                    </div>
                  ` : ''}
                </div>
              `).join('')}
            </div>

            <!-- FAQS & GAPS GRID -->
            <div class="faqs-gaps-grid">
              <!-- FAQS -->
              ${outline.aeoFaqs && outline.aeoFaqs.length > 0 ? `
                <div class="faqs-box">
                  <div class="sub-group-title title-amber">AEO & VOICE SEARCH FAQs (${outline.aeoFaqs.length})</div>
                  ${outline.aeoFaqs.map((faq, fIdx) => `
                    <div class="faq-item">
                      <div class="faq-q">Q${fIdx + 1}: ${faq.question}</div>
                      <div class="faq-a">A: ${faq.answer}</div>
                    </div>
                  `).join('')}
                </div>
              ` : ''}

              <!-- GAPS -->
              ${outline.contentGaps && outline.contentGaps.length > 0 ? `
                <div class="gaps-box">
                  <div class="sub-group-title title-emerald">MARKET GAPS EXPLOITED (${outline.contentGaps.length})</div>
                  ${outline.contentGaps.map(gap => `
                    <div class="gap-item">
                      ✨ ${gap}
                    </div>
                  `).join('')}
                </div>
              ` : ''}
            </div>

            <!-- STEP 4 FULL GENERATED WEBPAGE CONTENT -->
            ${page.generatedContent ? `
              <div class="generated-content-container">
                <div class="generated-content-header">
                  <span class="gen-badge">STEP 4 COMPLETE WEBPAGE CONTENT DELIVERABLE</span>
                  <span class="gen-word-count">${page.generatedContent.trim().split(/\s+/).length} Words Generated</span>
                </div>
                <div class="generated-content-body">
                  ${formatMarkdownToHtml(page.generatedContent)}
                </div>
              </div>
            ` : ''}
          ` : `
            <div class="no-outline-box">
              ⚠️ Page outline brief is currently being finalized for this page.
            </div>
          `}
        </div>
      `;
    }).join('\n');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Content Planning & Page Architecture Strategy — ${project.clientName}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;700&display=swap');

          @page {
            size: A4 portrait;
            margin: 10mm;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            box-sizing: border-box;
          }

          body {
            font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
            background: #090d16;
            color: #f8fafc;
            line-height: 1.5;
            margin: 0;
            padding: 0;
            font-size: 11.5px;
          }

          .no-print {
            background: linear-gradient(135deg, #0284c7, #4f46e5);
            color: white;
            padding: 14px;
            text-align: center;
            font-weight: 800;
            font-size: 13px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          }

          .no-print button {
            background: #ffffff;
            color: #0369a1;
            border: none;
            padding: 8px 18px;
            border-radius: 8px;
            font-weight: 800;
            cursor: pointer;
            margin-left: 14px;
          }

          .page-container {
            padding: 24px 32px;
            max-width: 980px;
            margin: 0 auto;
            background: #090d16;
          }

          @media print {
            .no-print { display: none !important; }
            .page-container { padding: 0 !important; max-width: 100% !important; }
            .page-break-before { page-break-before: always; }
          }

          /* HERO HEADER BANNER */
          .hero-banner {
            background: linear-gradient(135deg, #020617 0%, #1e1b4b 50%, #020617 100%);
            border-radius: 14px;
            padding: 28px;
            color: #ffffff;
            margin-bottom: 24px;
            border: 1px solid #312e81;
          }

          .hero-badge {
            display: inline-block;
            background: rgba(129, 140, 248, 0.2);
            border: 1px solid #818cf8;
            color: #c7d2fe;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 10px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 10px;
          }

          .hero-title {
            font-size: 24px;
            font-weight: 800;
            margin: 0 0 4px 0;
            color: #ffffff;
            letter-spacing: -0.5px;
          }

          .hero-subtitle {
            font-size: 14px;
            color: #38bdf8;
            font-weight: 700;
            margin-bottom: 14px;
          }

          .hero-meta {
            display: flex;
            gap: 20px;
            font-size: 11px;
            color: #94a3b8;
            border-top: 1px solid rgba(255,255,255,0.1);
            padding-top: 12px;
            flex-wrap: wrap;
            font-family: 'JetBrains Mono', monospace;
          }

          .hero-meta-item strong {
            color: #ffffff;
          }

          /* EXECUTIVE METHODOLOGY SECTION */
          .methodology-box {
            background: #0f172a;
            border: 1px solid #1e293b;
            border-radius: 14px;
            padding: 24px;
            margin-bottom: 28px;
          }

          .methodology-header {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 15px;
            font-weight: 800;
            color: #ffffff;
            margin-bottom: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .methodology-desc {
            font-size: 12px;
            color: #cbd5e1;
            line-height: 1.6;
            margin-bottom: 16px;
          }

          .methodology-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 14px;
          }

          .methodology-card {
            background: #090d16;
            border: 1px solid #1e293b;
            border-radius: 10px;
            padding: 14px 16px;
          }

          .methodology-card-title {
            font-size: 12px;
            font-weight: 800;
            color: #38bdf8;
            margin-bottom: 6px;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .methodology-card-text {
            font-size: 11.5px;
            color: #94a3b8;
            line-height: 1.5;
          }

          /* PAGE CARD */
          .page-card {
            background: #0f172a;
            border: 1px solid #1e293b;
            border-radius: 14px;
            padding: 24px;
            margin-bottom: 24px;
          }

          .page-title {
            font-size: 20px;
            font-weight: 800;
            color: #ffffff;
            margin: 4px 0;
          }

          .page-url code {
            font-family: 'JetBrains Mono', monospace;
            font-size: 11px;
            color: #38bdf8;
          }

          .badge {
            display: inline-block;
            padding: 3px 8px;
            border-radius: 6px;
            font-size: 10px;
            font-weight: 800;
            text-transform: uppercase;
          }

          .badge-amber {
            background: rgba(245, 158, 11, 0.15);
            color: #f59e0b;
            border: 1px solid rgba(245, 158, 11, 0.3);
          }

          .badge-cyan {
            background: rgba(56, 189, 248, 0.15);
            color: #38bdf8;
            border: 1px solid rgba(56, 189, 248, 0.3);
          }

          .word-count-badge {
            background: #090d16;
            border: 1px solid #1e293b;
            color: #10b981;
            padding: 6px 14px;
            border-radius: 8px;
            font-size: 12px;
            font-weight: 800;
            white-space: nowrap;
          }

          /* KEYWORD ARCHITECTURE BOX */
          .kw-arch-box {
            background: #090d16;
            border: 1px solid #1e293b;
            border-radius: 10px;
            padding: 12px 16px;
            margin-top: 12px;
          }

          .kw-arch-title {
            font-size: 10px;
            font-weight: 800;
            color: #64748b;
            letter-spacing: 0.5px;
            margin-bottom: 8px;
          }

          .kw-arch-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 12px;
          }

          .kw-label {
            font-size: 11px;
            color: #94a3b8;
            font-weight: 600;
          }

          .kw-value {
            font-size: 12px;
            color: #ffffff;
            font-weight: 700;
            margin-left: 4px;
          }

          .kw-sub {
            font-size: 10.5px;
            color: #64748b;
            margin-top: 2px;
          }

          /* META GRID */
          .meta-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin: 16px 0;
          }

          .meta-box {
            background: #090d16;
            border: 1px solid #1e293b;
            border-radius: 10px;
            padding: 14px;
          }

          .meta-box-label {
            font-size: 10px;
            font-weight: 800;
            margin-bottom: 6px;
            letter-spacing: 0.5px;
          }

          .label-blue { color: #38bdf8; }
          .label-amber { color: #f59e0b; }

          .meta-box-value {
            font-size: 12px;
            color: #f8fafc;
            line-height: 1.4;
          }

          /* H1 CARD */
          .h1-card {
            background: #090d16;
            border: 1.5px solid rgba(16, 185, 129, 0.4);
            border-radius: 10px;
            padding: 14px 18px;
            margin-bottom: 16px;
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .h1-badge {
            background: rgba(16, 185, 129, 0.15);
            border: 1px solid rgba(16, 185, 129, 0.3);
            color: #10b981;
            font-size: 10px;
            font-weight: 800;
            padding: 3px 8px;
            border-radius: 6px;
            white-space: nowrap;
          }

          .h1-text {
            font-size: 16px;
            font-weight: 800;
            color: #ffffff;
          }

          /* SECTIONS CONTAINER */
          .sections-container {
            margin-bottom: 18px;
          }

          .section-group-title {
            font-size: 11px;
            font-weight: 800;
            color: #38bdf8;
            letter-spacing: 0.5px;
            margin-bottom: 10px;
          }

          .section-item {
            background: #090d16;
            border: 1px solid #1e293b;
            border-radius: 10px;
            padding: 14px 16px;
            margin-bottom: 10px;
          }

          .section-item-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 6px;
          }

          .tag-badge {
            font-size: 10px;
            font-weight: 800;
            padding: 2px 6px;
            border-radius: 4px;
          }

          .tag-h2 {
            background: rgba(2, 132, 199, 0.15);
            color: #38bdf8;
            border: 1px solid rgba(56, 189, 248, 0.3);
          }

          .tag-h3 {
            background: rgba(168, 85, 247, 0.15);
            color: #c084fc;
            border: 1px solid rgba(192, 132, 252, 0.3);
          }

          .section-heading-text {
            font-size: 14px;
            font-weight: 800;
            color: #ffffff;
          }

          .section-purpose {
            font-size: 11.5px;
            color: #94a3b8;
            margin-bottom: 8px;
            font-style: italic;
          }

          .keypoints-list {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
          }

          .kp-pill {
            font-size: 11px;
            color: #cbd5e1;
            background: #070c16;
            border: 1px solid #1e293b;
            padding: 3px 8px;
            border-radius: 6px;
          }

          /* FAQS & GAPS GRID */
          .faqs-gaps-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
            margin-top: 16px;
          }

          .faqs-box, .gaps-box {
            background: #090d16;
            border: 1px solid #1e293b;
            border-radius: 10px;
            padding: 14px;
          }

          .sub-group-title {
            font-size: 11px;
            font-weight: 800;
            letter-spacing: 0.5px;
            margin-bottom: 10px;
          }

          .title-amber { color: #f59e0b; }
          .title-emerald { color: #10b981; }

          .faq-item {
            border-bottom: 1px solid #1e293b;
            padding-bottom: 8px;
            margin-bottom: 8px;
          }
          .faq-item:last-child {
            border-bottom: none;
            margin-bottom: 0;
            padding-bottom: 0;
          }

          .faq-q {
            font-size: 11.5px;
            font-weight: 700;
            color: #ffffff;
            margin-bottom: 3px;
          }

          .faq-a {
            font-size: 11px;
            color: #94a3b8;
          }

          .gap-item {
            font-size: 11px;
            color: #10b981;
            background: rgba(16, 185, 129, 0.08);
            border: 1px solid rgba(16, 185, 129, 0.2);
            padding: 8px 10px;
            border-radius: 6px;
            margin-bottom: 6px;
          }
          .gap-item:last-child { margin-bottom: 0; }

          .no-outline-box {
            background: #090d16;
            border: 1px dashed #1e293b;
            border-radius: 10px;
            padding: 20px;
            text-align: center;
            color: #f59e0b;
            font-weight: 700;
          }

          /* GENERATED WEBPAGE CONTENT STYLES */
          .generated-content-container {
            background: #090d16;
            border: 1px solid #1e293b;
            border-radius: 12px;
            padding: 20px;
            margin-top: 20px;
          }

          .generated-content-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 16px;
            padding-bottom: 10px;
            border-bottom: 1px solid #1e293b;
          }

          .gen-badge {
            background: rgba(16, 185, 129, 0.15);
            border: 1px solid rgba(16, 185, 129, 0.3);
            color: #10b981;
            font-size: 10.5px;
            font-weight: 800;
            padding: 4px 10px;
            border-radius: 6px;
            letter-spacing: 0.5px;
          }

          .gen-word-count {
            font-size: 11px;
            font-weight: 700;
            color: #38bdf8;
            background: #070c16;
            padding: 4px 10px;
            border-radius: 6px;
            border: 1px solid #1e293b;
          }

          .generated-content-body {
            color: #e2e8f0;
            line-height: 1.65;
            font-size: 12px;
          }

          .pdf-gen-h1 {
            font-size: 18px;
            font-weight: 800;
            color: #ffffff;
            margin: 18px 0 10px 0;
            border-bottom: 1px solid #1e293b;
            padding-bottom: 6px;
          }

          .pdf-gen-h2 {
            font-size: 15px;
            font-weight: 800;
            color: #38bdf8;
            margin: 16px 0 8px 0;
          }

          .pdf-gen-h3 {
            font-size: 13.5px;
            font-weight: 700;
            color: #f59e0b;
            margin: 14px 0 6px 0;
          }

          .pdf-gen-h4 {
            font-size: 12.5px;
            font-weight: 700;
            color: #c084fc;
            margin: 12px 0 6px 0;
          }

          .pdf-gen-p {
            margin: 0 0 12px 0;
            color: #cbd5e1;
          }

          .pdf-gen-ul, .pdf-gen-ol {
            margin: 0 0 12px 0;
            padding-left: 20px;
            color: #cbd5e1;
          }

          .pdf-gen-ul li, .pdf-gen-ol li {
            margin-bottom: 4px;
          }

          .generated-content-body a {
            color: #38bdf8;
            text-decoration: underline;
          }

          /* FOOTER */
          .pdf-footer {
            margin-top: 36px;
            border-top: 1px solid #1e293b;
            padding-top: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 10px;
            color: #64748b;
          }
        </style>
      </head>
      <body>
        <div class="no-print">
          📄 Content Planning & Page Architecture Deliverable Ready — 
          <button onclick="window.print()">Print / Save as PDF</button>
        </div>

        <div class="page-container">
          <!-- HERO HEADER BANNER -->
          <div class="hero-banner">
            <div class="hero-badge">EXECUTIVE CONTENT STRATEGY REPORT</div>
            <h1 class="hero-title">${project.clientName}</h1>
            <div class="hero-subtitle">Comprehensive Page Architecture & Content Blueprint</div>
            
            <div class="hero-meta">
              <div class="hero-meta-item">Website: <strong>${project.websiteUrl}</strong></div>
              <div class="hero-meta-item">Target Market: <strong>${project.targetedLocation}</strong></div>
              <div class="hero-meta-item">Industry Category: <strong>${project.category}</strong></div>
              <div class="hero-meta-item">Planned Pages: <strong>${pages.length} Pages (${completedOutlinesCount} Outlines Ready, ${pages.filter(p => p.generatedContent).length} Articles Generated)</strong></div>
              <div class="hero-meta-item">Total Word Blueprint: <strong>${totalWordCount.toLocaleString()} Words</strong></div>
              <div class="hero-meta-item">Generated: <strong>${dateStr}</strong></div>
            </div>
          </div>

          <!-- EXECUTIVE METHODOLOGY SECTION -->
          <div class="methodology-box">
            <div class="methodology-header">
              <span>🧠</span>
              <span>Strategic Content Architecture & Market Gap Methodology</span>
            </div>
            
            <div class="methodology-desc">
              To build a content framework that out-ranks top competitors and converts searchers into customers, we conducted exhaustive market research, competitor structural mapping, search intent alignment, and voice-search Q&A modeling for <strong>${project.clientName}</strong> in <strong>${project.targetedLocation}</strong>. The resulting architecture below is engineered to satisfy both modern search engine algorithms and prospective buyers.
            </div>

            <div class="methodology-grid">
              <div class="methodology-card">
                <div class="methodology-card-title">🔍 1. Competitor Gap Analysis</div>
                <div class="methodology-card-text">
                  We analyzed the top 10 ranking SERP competitor page structures and heading hierarchies (H1, H2, H3). We identified missing technical subtopics, unaddressed risks, missing local context, and content gaps across competitors to ensure our page depth exceeds top results by 20%+.
                </div>
              </div>

              <div class="methodology-card">
                <div class="methodology-card-title">💡 2. Customer Friction & Pain Point Solving</div>
                <div class="methodology-card-text">
                  Instead of thin promotional copy, each page section is systematically mapped to solve real customer anxieties (downtime risks, incorrect fitment, pricing surprises, fake review skepticism). Section purposes directly overcome purchasing objections.
                </div>
              </div>

              <div class="methodology-card">
                <div class="methodology-card-title">🎙️ 3. AEO & Voice Search Engineering</div>
                <div class="methodology-card-text">
                  Every page features 10 high-intent Q&A pairs modeled after Google People Also Ask (PAA), AnswerThePublic, and voice search queries. FAQ answers are concise (40–60 words) and direct for instant extraction in search snippets, ChatGPT, Gemini, Perplexity, and SearchGPT.
                </div>
              </div>

              <div class="methodology-card">
                <div class="methodology-card-title">🛡️ 4. Anti-Hallucination & Factual Safety</div>
                <div class="methodology-card-text">
                  To protect brand authority and prevent false AI statements, strict factual safety rules were enforced. Pricing claims use range hedging, testimonials use composite customer insights, and location details use verified regional factors.
                </div>
              </div>
            </div>
          </div>

          <!-- PAGE BREAKDOWN CARDS -->
          ${pagesHtml}

          <!-- FOOTER -->
          <div class="pdf-footer">
            <div>Confidential Strategic Deliverable • Prepared for <strong>${project.clientName}</strong></div>
            <div>Generated by Content Planning Engine • ${dateStr}</div>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
}

// Markdown to HTML formatting helper for PDF report
function formatMarkdownToHtml(markdown: string): string {
  if (!markdown) return '';
  return markdown
    .split(/\n\s*\n/)
    .map(paragraph => {
      const trimmed = paragraph.trim();
      if (!trimmed) return '';

      // Headings
      if (trimmed.startsWith('# ')) {
        return `<h1 class="pdf-gen-h1">${escapeHtml(trimmed.slice(2))}</h1>`;
      }
      if (trimmed.startsWith('## ')) {
        return `<h2 class="pdf-gen-h2">${escapeHtml(trimmed.slice(3))}</h2>`;
      }
      if (trimmed.startsWith('### ')) {
        return `<h3 class="pdf-gen-h3">${escapeHtml(trimmed.slice(4))}</h3>`;
      }
      if (trimmed.startsWith('#### ')) {
        return `<h4 class="pdf-gen-h4">${escapeHtml(trimmed.slice(5))}</h4>`;
      }

      // Unordered list
      if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
        const items = trimmed
          .split('\n')
          .map(item => `<li>${formatInlineMarkdown(escapeHtml(item.replace(/^[\*\-]\s+/, '')))}</li>`)
          .join('');
        return `<ul class="pdf-gen-ul">${items}</ul>`;
      }

      // Numbered list
      if (/^\d+\.\s+/.test(trimmed)) {
        const items = trimmed
          .split('\n')
          .map(item => `<li>${formatInlineMarkdown(escapeHtml(item.replace(/^\d+\.\s+/, '')))}</li>`)
          .join('');
        return `<ol class="pdf-gen-ol">${items}</ol>`;
      }

      // Standard paragraph
      return `<p class="pdf-gen-p">${formatInlineMarkdown(escapeHtml(trimmed))}</p>`;
    })
    .filter(Boolean)
    .join('\n');
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatInlineMarkdown(str: string): string {
  return str
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
}
